import React from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';

import { Cart } from 'catering/components/Cart';
import { RouteDrawer } from 'catering/components/Drawers';
import { RouteModalFooter } from 'catering/components/Modals';
import CartConfirmation from 'components/Cart/CartConfirmation';
import PageLoadSpinner from 'components/PageLoadSpinner';
import PrimaryCta from 'components/Buttons/PrimaryCta';

import {
  useGoToCartItem,
  useGoToMenuScreen,
  useGoToOrderSettings
} from 'catering/hooks/navigation';
import {
  useCart,
  useEmptyCart,
  useValidateCart,
  useRemoveCartItem,
  selectIsCartEmpty,
  useResetPickupAddress
} from 'catering/hooks/query';
import {
  getCartCheckoutButtonLabel,
  continueOrRefresh
} from 'catering/helpers/cart';
import {
  CART_CHECKOUT_LABEL,
  EMPTY_CART_CONFIRMATION_MODAL,
  CART_PICKUP_ADDRESS_CHANGED_LABEL
} from 'constants/cart';

const routeModalDialogContentStyle = {
  '&.MuiDialogContent-root': {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  }
};

function CartContainer() {
  const { locationSlug } = useParams();
  const goToCartItem = useGoToCartItem();
  const goToMenuScreen = useGoToMenuScreen();
  const goToOrderSettings = useGoToOrderSettings();

  const [checkoutButtonLabel, setCheckoutButtonLabel] = React.useState(
    CART_CHECKOUT_LABEL
  );
  const [validationError, setValidationError] = React.useState(null);
  const [
    isEmptyCartConfirmationOpen,
    setIsEmptyCartConfirmationOpen
  ] = React.useState(false);

  const { data: cart, isLoading, refetch: refetchCart } = useCart();
  const emptyCart = useEmptyCart();
  const removeCartItem = useRemoveCartItem();
  const resetPickupAddress = useResetPickupAddress();

  // Will validate the cart on component mount and whenever Cart updates
  useValidateCart({
    onSuccess: () => {
      setCheckoutButtonLabel(CART_CHECKOUT_LABEL);
      setValidationError(null);
    },
    onError: ({ message, code }) => {
      continueOrRefresh(code);
      setCheckoutButtonLabel(getCartCheckoutButtonLabel(code));
      setValidationError(message);
    }
  });

  const handleOpenEmptyCartConfirmation = () =>
    setIsEmptyCartConfirmationOpen(true);
  const handleCloseEmptyCartConfirmation = () =>
    setIsEmptyCartConfirmationOpen(false);

  const handleFulfillmentMethodChange = () => {
    goToOrderSettings({
      locationSlug,
      fulfillmentMethod: cart.catering_fulfillment_method
    });
  };

  const handleEditCartItem = cartItemId => goToCartItem(cartItemId);

  const handleEmptyCart = () => {
    emptyCart.mutate(null, {
      onSuccess: () => goToMenuScreen()
    });
  };

  const handleRemoveCartItem = cartItemId => {
    removeCartItem.mutate(cartItemId, {
      onSuccess: updateCart => {
        const isCartEmpty = selectIsCartEmpty(updateCart);
        if (isCartEmpty) {
          goToMenuScreen({ isRedirect: true });
        }
      },
      onError: () => {
        refetchCart();
      }
    });
  };

  const handleResetPickupAddress = () => {
    resetPickupAddress.mutate();
  };

  if (isLoading) {
    return <PageLoadSpinner />;
  }

  const isCartValid = !validationError;

  // We hide the Modal Dismiss Button when we're showing the Empty Cart Confirmation.
  const displayModalDismissButton = !isEmptyCartConfirmationOpen;
  // The RouterDrawer component renders the RouteModal on mobile devices.
  // Since we need to override styles on mobile, this is how can accomplish that.
  const RouteModalProps = isEmptyCartConfirmationOpen
    ? {
        sx: routeModalDialogContentStyle
      }
    : {};

  return (
    <RouteDrawer
      onClose={goToMenuScreen}
      displayDismissButton={displayModalDismissButton}
      RouteModalProps={RouteModalProps}
    >
      <>
        {isEmptyCartConfirmationOpen ? (
          <Box padding={2.5}>
            <CartConfirmation
              onConfirm={handleEmptyCart}
              onCancel={handleCloseEmptyCartConfirmation}
              modalType={EMPTY_CART_CONFIRMATION_MODAL}
            />
          </Box>
        ) : (
          <Cart
            cart={cart}
            checkoutAction={
              <RouteModalFooter>
                {checkoutButtonLabel === CART_PICKUP_ADDRESS_CHANGED_LABEL ? (
                  <Box width="100%">
                    <PrimaryCta
                      id="cart-checkout-button" // id is used for a11y jump to checkout button
                      component="a"
                      fullWidth
                      handleClick={handleResetPickupAddress}
                      label={checkoutButtonLabel}
                    />
                  </Box>
                ) : (
                  <Box width="100%">
                    <PrimaryCta
                      id="cart-checkout-button"
                      component="a"
                      disabled={!isCartValid}
                      fullWidth
                      href={cart.checkout_url}
                      label={checkoutButtonLabel}
                    />
                  </Box>
                )}
              </RouteModalFooter>
            }
            validationError={validationError}
            onFulfillmentMethodChange={handleFulfillmentMethodChange}
            onRemoveCartItem={handleRemoveCartItem}
            onEditCartItem={handleEditCartItem}
            onOpenEmptyCartConfirmation={handleOpenEmptyCartConfirmation}
          />
        )}
      </>
    </RouteDrawer>
  );
}

export default CartContainer;
